import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { AlertService } from '../../../../../services/alert.service';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TemplatesService } from '../../../../../services/templates.service';
import { listTypeRCS } from '../../constants/data.constants';
import { PROFILES } from '../../../../../constants';
import { useSession } from '../../../../../hooks/use-session.hook';
import Select from 'react-select';
import InvalidFeedback from '../../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../../components/forms/icon.component';
import Title from '../../../../../components/forms/titles.component';
import useDetectedVariables from '../../../../../hooks/use-detected-varibales';
import EventsSection, { getChannelEventsConfig } from './events.component';
import { useLocation } from 'react-router-dom';

function ChannelRcs({ onValidationChange }: { onValidationChange?: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const { detectedVariables } = useDetectedVariables();
  const location = useLocation();
  const showEvents = location.pathname.includes('sendCampaigns/flows-campaigns');
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const [optionsAgents, setOptionsAgents] = useState<{ value: string; label: string }[]>([]);
  const [templatesRCS, setTemplatesRCS] = useState<any[]>([]);
  const [previewRCS, setPreviewRCS] = useState<any>();
  const [emptyAgentsRCS, setEmptyAgentsRCS] = useState(false);
  const [emptyTemplatesRCS, setEmptyTemplatesRCS] = useState(false);
  const [variablesNotFound, setVariablesNotFound] = useState<Array<string>>([]);
  const [eventsValid, setEventsValid] = useState(true);

  const { control, watch, setValue } = useFormContext();

  const formValues = useMemo(() => {
    const values = watch();
    return {
      headersOptions: values.headersOptions,
      channelsAvailable: values.channelsAvailable,
      typeCampaign: values.typeCampaign,
      account: values.account,
      channel: values.channel,
      typeRCS: values.typeRCS,
      templatesRCS: values.templatesRCS,
      agentsRCS: values.agentsRCS,
      channelfallback: values.channelfallback,
      accountCahnnels: values.accountCahnnels,
      typeCampaigns: values.typeCampaigns,
      channelFlows: values.channelFlows,
      events: values.events
    };
  }, [watch()]);

  const templatesRCSMessage = useMemo(() => {
    if (!emptyTemplatesRCS) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene plantillas registradas de este tipo de RCS'
      : 'La cuenta seleccionada en el paso 1 no tiene plantillas registradas de este tipo de RCS.';
  }, [emptyTemplatesRCS, session.profile]);

  const agentsRCSMessage = useMemo(() => {
    if (!emptyAgentsRCS) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene un agente registrado para este tipo de RCS'
      : 'La cuenta seleccionada en el paso 1 no tiene un agente registrado para este tipo de RCS.';
  }, [emptyAgentsRCS, session.profile]);

  const typeLabelMap = useMemo(
    () => ({
      rcs_basic: 'Básico',
      rcs_single: 'Simple'
    }),
    []
  );

  const typeLabel = useMemo(
    () => typeLabelMap[formValues.typeRCS as keyof typeof typeLabelMap] || 'Conversacional',
    [formValues.typeRCS, typeLabelMap]
  );

  const rcsTypeOptions = useMemo(() => {
    const baseOptions =
      formValues.typeCampaign === 'FLOW'
        ? listTypeRCS.filter((opt) => opt.value === 'rcs_conversational')
        : listTypeRCS;

    return baseOptions.filter((opt) => formValues.channelsAvailable?.includes(opt.value));
  }, [formValues.typeCampaign, formValues.channelsAvailable]);

  const fallbackChannelOptions = useMemo(() => {
    if (!formValues.channelFlows || !Array.isArray(formValues.channelFlows)) return [];

    const channelLabels: Record<string, string> = {
      wa: 'WhatsApp',
      sms: 'SMS',
      mail: 'Email'
    };

    return formValues.channelFlows
      .filter((channel) => channel !== 'rcs')
      .map((channel) => ({
        value: channel,
        label: channelLabels[channel] || channel.toUpperCase()
      }));
  }, [formValues.channelFlows]);

  const followUpTemplateOptions = useMemo(() => {
    if (!templatesRCS || !Array.isArray(templatesRCS)) return [];
    return templatesRCS.filter((template) => template.value !== formValues.templatesRCS);
  }, [templatesRCS, formValues.templatesRCS]);

  const eventsConfig = useMemo(() => {
    return getChannelEventsConfig('rcs', fallbackChannelOptions, followUpTemplateOptions);
  }, [fallbackChannelOptions, followUpTemplateOptions]);

  const handleCheckVariables = useCallback(() => {
    const response = detectedVariables({ previewRCS, optionsExamples: formValues.headersOptions });
    setVariablesNotFound(response?.variables ? response?.variables : []);
  }, [detectedVariables, previewRCS, formValues.headersOptions]);

  const getTemplatesRCS = useCallback(async () => {
    if (!formValues.typeRCS || !formValues.account) return;

    try {
      const response = await TemplatesService.getTemplatesAccountRCS(formValues.account);
      setEmptyTemplatesRCS(!response || response.length === 0);
      const allTemplates = Object.values(response).flat() as any[];
      const filtered = allTemplates.filter((template) => {
        const type = template.type?.toLowerCase();
        const isActive = template.status === true;

        if (formValues.typeRCS === 'rcs_basic') return type === 'basic' && isActive;
        if (formValues.typeRCS === 'rcs_single') return type === 'single' && isActive;
        if (formValues.typeRCS === 'rcs_conversational')
          return type === 'conversational' && isActive;

        return false;
      });

      const finalTemplates = filtered.map(({ _id, name, ...rest }) => ({
        value: _id,
        label: name,
        fullTemplate: { _id, name, ...rest }
      }));

      setTemplatesRCS(finalTemplates);
    } catch (errorCode: any) {
      setValue('templatesRCS', '');
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  }, [formValues.typeRCS, formValues.account, setValue]);

  useEffect(() => {
    const currentChannel =
      formValues.typeCampaigns === 'FLOW'
        ? formValues.accountCahnnels?.['rcs_conversational']
        : formValues.accountCahnnels?.[formValues.typeRCS];

    if (Array.isArray(currentChannel)) {
      const filtered = currentChannel.filter(
        (item: any) => item.status === true && item.config?.status === true
      );

      if (filtered.length === 0) {
        setOptionsAgents([]);
        setEmptyAgentsRCS(true);
        return;
      }

      const options = filtered.map(({ config }: any) => ({
        value: config._id,
        label: config.name
      }));

      setOptionsAgents(options);
      setEmptyAgentsRCS(false);
    } else {
      setOptionsAgents([]);
      setEmptyAgentsRCS(true);
    }
  }, [
    formValues.account,
    formValues.channel,
    formValues.channelfallback,
    formValues.typeRCS,
    formValues.typeCampaigns,
    formValues.accountCahnnels
  ]);

  useEffect(() => {
    handleCheckVariables();
    setValue('previewTemplateRcs', previewRCS);
  }, [previewRCS, handleCheckVariables, setValue]);

  useEffect(() => {
    const shouldClearFields =
      formValues.typeCampaign !== 'FALLBACK' ||
      (formValues.typeCampaign === 'FALLBACK' && formValues.channel !== 'rcs');

    if (shouldClearFields) {
      setValue('typeRCS', '');
      setValue('templatesRCS', '');
      setValue('agentsRCS', '');
      setTemplatesRCS([]);
    }
  }, [formValues.channel, formValues.channelfallback, formValues.typeCampaign, setValue]);

  useEffect(() => {
    if (formValues.account) {
      setValue('templatesRCS', '');
      setValue('agentsRCS', '');
      setTemplatesRCS([]);
      setEmptyTemplatesRCS(false);
      setEmptyAgentsRCS(false);
    }
  }, [formValues.account, setValue]);

  useEffect(() => {
    if (formValues.typeRCS && formValues.account) {
      setValue('templatesRCS', '');
      setValue('agentsRCS', '');
      setTemplatesRCS([]);
      setEmptyTemplatesRCS(false);
      setEmptyAgentsRCS(false);
      getTemplatesRCS();
    }
  }, [formValues.typeRCS, formValues.account, setValue, getTemplatesRCS]);

  useEffect(() => {
    if (formValues.templatesRCS) {
      const selectedTemplate = templatesRCS.find(
        (template) => template.value === formValues.templatesRCS
      );
      if (selectedTemplate?.fullTemplate) {
        setPreviewRCS(selectedTemplate.fullTemplate);
      } else {
        setPreviewRCS([]);
      }
    } else {
      setPreviewRCS([]);
    }
  }, [formValues.templatesRCS, templatesRCS]);

  useEffect(() => {
    const baseValidation =
      !!formValues.typeRCS && !!formValues.agentsRCS && !!formValues.templatesRCS;

    const isValid = baseValidation && (showEvents ? eventsValid : true);

    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(isValid);
    }
  }, [formValues.typeRCS, formValues.agentsRCS, formValues.templatesRCS, eventsValid]);

  return (
    <>
      <div className="separator separator-dashed separator-content text-dark mb-15">
        <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="w-30px logo-minimize" />
      </div>
      <div className="fv-row mb-10 z-index-3">
        <div className="text-left mb-3">
          <Title
            icon="ki-messages"
            title={`Configuración RCS ${typeLabel}`}
            subtitle={''}
            type="info"
          />
        </div>
        <div className="d-flex flex-stack cursor-pointer mb-5">
          <div className="d-flex align-items-center">
            <span className="symbol symbol-label"></span>
            <div className="d-flex flex-column">
              <span className="fw-bolder fs-3 required">Tipo de RCS</span>
              <span className="fs-6">Selecciona el tipo de RCS.</span>
            </div>
          </div>
        </div>
        <Controller
          control={control}
          name="typeRCS"
          rules={{ required: formValues.channel === 'rcs' }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <Select
                options={rcsTypeOptions}
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? t('general.no-options', { inputValue }) : t('general.no-options')
                }
                className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                classNamePrefix="form-select-custom"
                placeholder={'Selecciona tipo de RCS'}
                onChange={(
                  selectedOption: {
                    value: string;
                    label: string;
                  } | null
                ) => {
                  onChange(selectedOption ? selectedOption.value : null);
                }}
                value={
                  listTypeRCS.find(
                    (option: { value: string; label: string }) => option.value === value
                  ) || null
                }
              />
              <InvalidFeedback error={error} />
            </>
          )}
        />
      </div>
      <div className="mb-5 mb-xxl-8">
        <div className="pt-1">
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Agentes RCS</span>
                <span className="fs-6">Elige el agente para enviar tu mensaje.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="agentsRCS"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={optionsAgents}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona un agente RCS'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          optionsAgents.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
              {emptyAgentsRCS && (
                <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                  <Icon icon="ki-information-2" size="fs-2hx" type="info" />
                  <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                    <h5 className="mb-1">¡ Atención !</h5>
                    <span>{agentsRCSMessage}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Plantillas</span>
                <span className="fs-6">Elige la plantilla del mensaje.</span>
              </div>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="templatesRCS"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={templatesRCS}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona tu plantilla'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          templatesRCS.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
              {emptyTemplatesRCS && (
                <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                  <Icon icon="ki-information-2" size="fs-2hx" type="info" />
                  <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                    <h5 className="mb-1">¡ Atención !</h5>
                    <span>{templatesRCSMessage}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {Array.isArray(variablesNotFound) && variablesNotFound.length > 0 && (
            <div className="alert alert-dismissible bg-light-danger border border-danger border-dashed border-bold d-flex flex-column flex-sm-row p-5 mb-10">
              <Icon
                icon="ki-information-2"
                size="fs-3x"
                type="danger"
                className="me-4 mb-5 mb-sm-0"
              />
              <div className="d-flex flex-column pe-0 pe-sm-10">
                <h5 className="mb-4">
                  En la plantilla RCS seleccionada se encontraron variables que no existen como
                  columnas en el archivo base que subiste.
                </h5>
                <h5 className="mb-3">Las variables son:</h5>
                <ul className="text-danger fw-bolder">
                  {variablesNotFound.map((variable, index) => (
                    <li key={index}>{`{{${variable}}}`}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {showEvents ? (
          <EventsSection
            channel="rcs"
            events={eventsConfig}
            fallbackChannelOptions={fallbackChannelOptions}
            templateOptions={followUpTemplateOptions}
            onValidationChange={setEventsValid}
          />
        ) : null}
      </div>
    </>
  );
}

export default ChannelRcs;
